import * as React from "react";
import { cn } from "../lib/utils.js";

/**
 * BottomNav — fixed primary navigation for mobile.
 *
 * Renders a single tab row at the bottom of the viewport with safe-area
 * padding. Pages don't need to leave space for it — pair with
 * <BottomNavSpacer /> in scroll containers that should not bleed under it.
 *
 * Each item is rendered as a child slot so callers can plug NavLink, <a>, or
 * <button>. Item APIs stay un-opinionated.
 */
export function BottomNav({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <nav
      aria-label="Primary"
      className={cn(
        "fixed inset-x-0 bottom-0 z-30 border-t bg-background pb-safe md:hidden",
        className,
      )}
    >
      <ul className="grid h-bottom-nav grid-flow-col auto-cols-fr items-stretch">
        {children}
      </ul>
    </nav>
  );
}

/**
 * Render content inside a single BottomNav cell. The actual link/button is
 * passed in as children so the consumer chooses NavLink/anchor/button.
 */
export function BottomNavItem({
  active,
  icon,
  label,
  children,
  className,
}: {
  active?: boolean;
  icon: React.ReactNode;
  label: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <li className="contents">
      <div
        className={cn(
          "relative flex h-full min-w-touch items-center justify-center",
          className,
        )}
      >
        {children ?? (
          <BottomNavLabel active={active} icon={icon} label={label} />
        )}
      </div>
    </li>
  );
}

/**
 * Visual content for a bottom-nav cell. Used internally and exported for
 * consumers wiring up react-router NavLink (which needs full control of the
 * anchor element).
 */
export function BottomNavLabel({
  active,
  icon,
  label,
}: {
  active?: boolean;
  icon: React.ReactNode;
  label: React.ReactNode;
}): React.ReactElement {
  return (
    <span
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-1 text-xs font-medium transition-colors",
        active ? "text-foreground" : "text-muted-foreground",
      )}
    >
      <span
        className={cn(
          "flex h-6 w-6 items-center justify-center",
          active ? "" : "opacity-80",
        )}
      >
        {icon}
      </span>
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * Empty spacer matching the bottom-nav height + safe-area inset. Drop into
 * the bottom of long scroll regions so content never sits under the nav.
 */
export function BottomNavSpacer({ className }: { className?: string }): React.ReactElement {
  return (
    <div
      aria-hidden
      className={cn("h-bottom-nav pb-safe md:hidden", className)}
    />
  );
}
